import { http, notify } from 'utils'
import { checkEmail } from 'utils/errorHelpers'
import { pick, concat } from 'lodash'

// client mutation imports.
import { SET_CLIENT, SET_CLIENTS, SET_CLIENTS_COUNTS, SET_CLIENTS_COMPANIES, SET_CLIENTS_PAGINATION, ADD_CLIENTS, RESET_CLIENTS, RESET_CLIENT } from 'vx/mutations'

const modules = {}

// client state.
const state = {
  client: null,
  clients: [],
  clientsCounts: {
    all: 0,
    active: 0,
    inactive: 0
  },
  pagination: {}
}

// client state getters.
const getters = {
  /**
   * Determine if we have a single client.
   *
   * @return bool
   */
  haveClient: (state) => {
    return state.booking !== null &&
      state.client !== null
  },

  /**
   * Determine if we have no more client to load.
   *
   * @return bool
   */
  haveNoMoreClients: (state) => {
    return state.pagination.current_page >= state.pagination.last_page ||
      state.pagination.total === 0
  }
}

// client actions
const actions = {
  getClient ({ commit }, id) {
    return http.get(`clients/${id}`)
      .then((response) => {
        commit(SET_CLIENT, response.data)

        return response
      })
  },

  getClientsCounts ({ commit }, params) {
    return http.get('client-counts', { params })
      .then((response) => {
        commit(SET_CLIENTS_COUNTS, response.data)

        return response
      })
  },

  getClients ({ commit }, params = {}) {
    return http.get('clients', { params })
      .then((response) => {
        commit(SET_CLIENTS, response.data.data)
        commit(SET_CLIENTS_PAGINATION, response.data.meta)

        return response
      })
  },

  getCompanies ({ commit }, params = {}) {
    return http.get('clients/company', { params })
      .then((response) => {
        commit(SET_CLIENTS_COMPANIES, response.data.data)

        return response
      })
  },

  /**
   * Get next set of paginated bookings.
   *
   * @return Promise
   */
  getNextClients ({ commit, state, getters }, params = {}) {
    if (getters.haveNoMoreClients) {
      return Promise.reject(new Error('There are no more bookings to load.'))
    }

    params.page = state.pagination.current_page + 1

    return http.get(`clients`, { params })
      .then((response) => {
        commit(ADD_CLIENTS, response.data.data)
        commit(SET_CLIENTS_PAGINATION, response.data.meta)

        return response
      })
  },

  resetClient ({ commit }) {
    commit(RESET_CLIENT)
  },

  resetClients ({ commit }) {
    commit(RESET_CLIENTS)
  },

  storeClient ({ commit }, payload) {
    return http.post(`clients`, payload)
      .catch(error => {
        if (checkEmail(error)) {
          notify.error('That email has already been taken.')
        } else {
          notify.error('Something went wrong. Please check your input and try again.')
        }
      })
  }
}

// client state mutations.
const mutations = {
  [SET_CLIENT] (state, client) {
    state.client = client
  },

  [SET_CLIENTS] (state, clients) {
    state.clients = clients
  },

  [SET_CLIENTS_COUNTS] (state, clientsCounts) {
    state.clientsCounts = clientsCounts
  },

  [SET_CLIENTS_COMPANIES] (state, clientsCompanies) {
    state.clientsCompanies = clientsCompanies
  },

  [SET_CLIENTS_PAGINATION] (state, pagination) {
    state.pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  },

  [ADD_CLIENTS] (state, clients) {
    state.clients = concat(state.clients, clients)
  },

  [RESET_CLIENTS] (state) {
    state.clients = []
    state.pagination = {}
  },

  [RESET_CLIENT] (state) {
    state.client = null
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, modules, state, getters, actions, mutations
}
